/**
 * MCPLandingSection — "AI Workforce Operations" pitch on the landing page.
 *
 * Combines five subsections per the product brief:
 *   1. Capabilities ("Your workforce data. Inside ChatGPT & Claude.")
 *   2. Included with ReStaff (checklist)
 *   3. Why ReStaff AI is different (comparison)
 *   4. Enterprise AI security (guarantee rows)
 *   5. Workflow visualization (Connect → OAuth → Ask → Receive)
 *
 * Pure additive presentation — no routing, schema, OAuth, or MCP backend
 * changes. Reuses existing Tailwind/lucide visual identity.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Users, CalendarDays, BarChart3, Plane, ShieldCheck, Sparkles,
  CheckCircle2, BookOpen, MessagesSquare, ArrowDown,
  CircleDot, Lock,
} from 'lucide-react';

const CAPABILITIES = [
  {
    icon: Users,
    title: 'Staff Directory',
    prompts: [
      'Show staff directory',
      'Find employees by role',
      'Show employee locations',
    ],
  },
  {
    icon: CalendarDays,
    title: 'Scheduling',
    prompts: [
      "Show today's schedule",
      'Where are staffing gaps?',
      'Who approaches overtime?',
    ],
  },
  {
    icon: BarChart3,
    title: 'Labor Analytics',
    prompts: [
      'Summarize labor trends',
      'Show overtime analytics',
      'Compare workforce performance',
    ],
  },
  {
    icon: Plane,
    title: 'Leave Management',
    prompts: [
      'Who is on leave next week?',
      'Show leave balances',
    ],
  },
];

const SECURITY_QUICK = [
  'OAuth secured',
  'Tenant isolated',
  'Role permissions enforced',
  'No cross-company access',
];

const INCLUDED = [
  'Payroll',
  'Scheduling',
  'Staff Management',
  'Leave Tracking',
  'Labor Analytics',
  'ChatGPT MCP Integration',
  'Claude MCP Integration',
  'Workforce AI Queries',
  'Secure OAuth Access',
];

const COMPARISON = [
  { trad: 'Dashboards only',         restaff: 'AI + dashboards' },
  { trad: 'Manual reporting',         restaff: 'Ask AI directly' },
  { trad: 'CSV exports only',         restaff: 'Natural-language workforce intelligence' },
  { trad: 'Separate AI tools',        restaff: 'Built-in MCP integrations' },
];

const SECURITY_FULL = [
  { name: 'OAuth secured',           desc: 'Industry-standard OAuth 2.1 + PKCE. No shared secrets, no API keys to manage.' },
  { name: 'Tenant isolation',        desc: 'Every MCP query is filtered by your business at the database layer.' },
  { name: 'Scope permissions',       desc: 'Five granular read-only scopes. Approve only what each AI needs.' },
  { name: 'Role enforcement',        desc: 'Only owners and approved managers can connect AI assistants.' },
  { name: 'Audit retention',         desc: 'Every AI query is logged by tenant, scope, IP, and tool — CSV exportable.' },
  { name: 'No cross-company visibility', desc: 'Even super-admins resolve to a single tenant when using MCP. Period.' },
];

const FLOW = [
  { label: 'Connect ChatGPT or Claude', sub: 'in Settings → AI Connections' },
  { label: 'Approve OAuth scopes',       sub: 'pick exactly what to share' },
  { label: 'Ask in natural language',    sub: '"Show today\'s schedule"' },
  { label: 'Receive tenant-scoped data', sub: 'never any other business' },
];

export function MCPLandingSection() {
  return (
    <section
      id="ai-operations"
      className="py-16 md:py-24 px-4 bg-gradient-to-b from-slate-50 to-white"
      aria-label="AI Workforce Operations"
      data-testid="mcp-landing-section"
    >
      <div className="max-w-7xl mx-auto">

        {/* ─── 1. Capabilities ────────────────────────────────── */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-3 py-1 mb-4">
            <Sparkles size={13} className="text-orange-500" />
            <span className="text-[10px] md:text-xs font-semibold text-orange-700 uppercase tracking-wide">
              AI Workforce Operations
            </span>
          </div>
          <h2
            className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Manrope' }}
            data-testid="mcp-capabilities-heading"
          >
            Your workforce data.{' '}
            <span className="text-orange-500">Inside ChatGPT &amp; Claude.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            ReStaff exposes a secure Model Context Protocol layer so your AI of choice can answer real workforce questions — using your real data, scoped to your tenant only.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-testid="mcp-capability-cards">
          {CAPABILITIES.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-orange-200 hover:shadow-md transition-all"
                data-testid={`mcp-capability-${c.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center mb-3">
                  <Icon size={18} className="text-orange-500" />
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-3">{c.title}</h3>
                <ul className="space-y-1.5">
                  {c.prompts.map((p) => (
                    <li key={p} className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5 leading-snug">
                      “{p}”
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Security card sits next to the four capability cards */}
          <div
            className="bg-slate-900 text-white rounded-2xl p-5 sm:col-span-2 lg:col-span-4 mt-4"
            data-testid="mcp-quick-security-card"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                  <ShieldCheck size={18} className="text-emerald-300" />
                </div>
                <div>
                  <div className="text-base font-bold">Secure by design</div>
                  <div className="text-xs text-slate-300">AI only ever sees your tenant's data.</div>
                </div>
              </div>
              <div className="flex-1 flex flex-wrap gap-1.5 md:justify-end">
                {SECURITY_QUICK.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/10 border border-white/15 rounded-full px-2.5 py-1"
                  >
                    <CheckCircle2 size={12} className="text-emerald-300" />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── 2. Included with ReStaff ───────────────────────── */}
        <div className="mt-20 md:mt-28" data-testid="mcp-included-block">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              Everything included with ReStaff
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600">
              No add-ons, no per-message billing, no extra tier for AI access.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-sm">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {INCLUDED.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-2.5 text-sm text-slate-700"
                  data-testid={`mcp-included-${f.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ─── 3. Why ReStaff AI Is Different ─────────────────── */}
        <div className="mt-20 md:mt-28" data-testid="mcp-comparison-block">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              Why ReStaff AI is different
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600">
              Most workforce platforms bolt AI on. ReStaff makes it the operating layer.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-2 bg-slate-50 border-b border-slate-200">
              <div className="px-5 md:px-8 py-4 text-xs md:text-sm font-bold text-slate-600 uppercase tracking-wide">
                Traditional Workforce Software
              </div>
              <div className="px-5 md:px-8 py-4 text-xs md:text-sm font-bold text-orange-700 uppercase tracking-wide bg-orange-50/60">
                ReStaff
              </div>
            </div>
            {COMPARISON.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-2 ${i < COMPARISON.length - 1 ? 'border-b border-slate-100' : ''}`}
                data-testid={`mcp-comparison-row-${i}`}
              >
                <div className="px-5 md:px-8 py-4 md:py-5 text-sm text-slate-500 leading-snug">
                  {row.trad}
                </div>
                <div className="px-5 md:px-8 py-4 md:py-5 text-sm font-semibold text-slate-900 leading-snug flex items-start gap-2 bg-orange-50/40">
                  <Sparkles size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                  <span>{row.restaff}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── 4. Enterprise AI Security ──────────────────────── */}
        <div className="mt-20 md:mt-28" data-testid="mcp-security-block">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-4">
              <Lock size={13} className="text-emerald-600" />
              <span className="text-[10px] md:text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                Enterprise AI Security
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              Built for businesses that can't compromise on access
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECURITY_FULL.map((r) => (
              <div
                key={r.name}
                className="bg-white border border-slate-200 rounded-2xl p-5"
                data-testid={`mcp-security-${r.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start gap-3">
                  <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-600 leading-relaxed mt-1">{r.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── 5. Workflow visualization ──────────────────────── */}
        <div className="mt-20 md:mt-28" data-testid="mcp-flow-block">
          <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              How it works — in 4 steps
            </h2>
            <p className="mt-3 text-sm md:text-base text-slate-600">
              From zero to AI-driven workforce queries in under a minute.
            </p>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 md:p-12 text-white">
            <div className="grid lg:grid-cols-4 gap-5 md:gap-3 lg:items-stretch">
              {FLOW.map((s, i) => (
                <React.Fragment key={i}>
                  <div
                    className="bg-white/5 border border-white/10 rounded-2xl p-5 flex-1"
                    data-testid={`mcp-flow-step-${i + 1}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </div>
                      <CircleDot size={12} className="text-orange-300" />
                    </div>
                    <div className="text-sm font-bold leading-snug">{s.label}</div>
                    <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.sub}</div>
                  </div>
                  {i < FLOW.length - 1 && (
                    <div className="flex items-center justify-center lg:px-0">
                      <ArrowDown size={20} className="text-orange-400 lg:-rotate-90" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Sample example chip — natural-language demo */}
            <div className="mt-8 md:mt-10 max-w-2xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                <MessagesSquare size={13} />
                <span>Try it after connecting</span>
              </div>
              <code className="block text-sm md:text-base font-mono text-orange-200 leading-relaxed">
                "Show me today's schedule and flag anyone over 35 hours this week."
              </code>
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8" data-testid="mcp-flow-trial-btn">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/help/mcp">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8 bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="mcp-flow-guide-btn">
                  <BookOpen size={16} className="mr-2" />
                  MCP Setup Guide
                </Button>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
